import React, { useEffect, useState } from 'react';
import { WarningCircle, X } from '@phosphor-icons/react';

// A small in-app confirmation modal. We use this instead of `window.confirm` because
// several browsers (Brave, Firefox with the "prevent additional dialogs" checkbox)
// silently suppress native confirms, leaving the user with no feedback when they
// click a destructive action like "Clear all" or the trash icon.
//
// Usage:
//   const [open, setOpen] = useState(false);
//   <ConfirmDialog open={open} onCancel={() => setOpen(false)} onConfirm={doIt}
//                  title="Delete bundle?" message="..." confirmLabel="Delete" />
//
// For order-submission flows, set `requireAcceptance` to gate the confirm button
// behind a T&Cs acceptance checkbox. The dialog renders a viewable link to the PDF.

export const ConfirmDialog = ({
  open,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = true,
  busy = false,
  onConfirm,
  onCancel,
  testIdPrefix = 'confirm-dialog',
  requireAcceptance = false,
  acceptanceLabel = null,  // JSX rendered next to the checkbox
  // Optional opt-in secondary checkbox (NOT required to confirm). Used e.g. for
  // a "request prepayment terms" toggle on order submission. When ticked, the
  // boolean is passed up to the parent via `onConfirm({secondaryAccepted: true})`.
  secondaryAcceptanceEnabled = false,
  secondaryAcceptanceLabel = null,
  // Optional free-text input (e.g. a customer PO / reference). When enabled the
  // value is passed back on confirm as `reference`. Empty string when blank.
  referenceInputEnabled = false,
  referenceInputLabel = 'Your reference (optional)',
  referenceInputPlaceholder = '',
}) => {
  const [accepted, setAccepted] = useState(false);
  const [secondaryAccepted, setSecondaryAccepted] = useState(false);
  const [reference, setReference] = useState('');

  // Reset acceptance every time the dialog is (re)opened so users always re-affirm.
  useEffect(() => {
    if (open) {
      setAccepted(false);
      setSecondaryAccepted(false);
      setReference('');
    }
  }, [open]);

  const canConfirm = !busy && (!requireAcceptance || accepted);
  const tryConfirm = () => {
    if (canConfirm) onConfirm?.({ secondaryAccepted, reference: reference.trim() });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.();
      if (e.key === 'Enter' && canConfirm) tryConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel, canConfirm]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid={`${testIdPrefix}`}
      onClick={() => !busy && onCancel?.()}
    >
      <div
        className="bg-surface border border-border-color rounded-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-5 border-b border-border-color">
          <WarningCircle
            size={24}
            weight="duotone"
            className={destructive ? 'text-danger shrink-0' : 'text-gold shrink-0'}
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-text-primary" data-testid={`${testIdPrefix}-title`}>{title}</h3>
            {message && (
              <p className="text-sm text-text-secondary mt-1 whitespace-pre-line" data-testid={`${testIdPrefix}-message`}>
                {message}
              </p>
            )}
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testIdPrefix}-close`}
            className="text-text-secondary hover:text-text-primary disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        {referenceInputEnabled && (
          <div className="px-5 py-4 border-b border-border-color">
            <label
              className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold block mb-1"
              htmlFor={`${testIdPrefix}-reference-input`}
            >
              {referenceInputLabel}
            </label>
            <input
              id={`${testIdPrefix}-reference-input`}
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              disabled={busy}
              placeholder={referenceInputPlaceholder}
              data-testid={`${testIdPrefix}-reference-input`}
              maxLength={64}
              className="w-full bg-black border border-border-color rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary/60 focus:border-gold focus:outline-none"
            />
          </div>
        )}
        {requireAcceptance && (
          <label className="flex items-start gap-3 px-5 py-4 border-b border-border-color cursor-pointer hover:bg-surface-hover/40">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              disabled={busy}
              data-testid={`${testIdPrefix}-accept-terms`}
              className="mt-0.5 accent-gold w-4 h-4 shrink-0"
            />
            <span className="text-sm text-text-primary leading-relaxed">{acceptanceLabel}</span>
          </label>
        )}
        {secondaryAcceptanceEnabled && (
          <label className="flex items-start gap-3 px-5 py-4 border-b border-border-color cursor-pointer hover:bg-surface-hover/40">
            <input
              type="checkbox"
              checked={secondaryAccepted}
              onChange={(e) => setSecondaryAccepted(e.target.checked)}
              disabled={busy}
              data-testid={`${testIdPrefix}-accept-secondary`}
              className="mt-0.5 accent-gold w-4 h-4 shrink-0"
            />
            <span className="text-sm text-text-secondary leading-relaxed">{secondaryAcceptanceLabel}</span>
          </label>
        )}
        <div className="flex items-center justify-end gap-2 p-4">
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testIdPrefix}-cancel`}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={tryConfirm}
            disabled={!canConfirm}
            data-testid={`${testIdPrefix}-confirm`}
            title={requireAcceptance && !accepted ? 'Tick the Terms of Trade checkbox to continue.' : undefined}
            className={`px-4 py-2 text-sm font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
              destructive
                ? 'bg-danger text-white hover:bg-danger/90'
                : 'bg-gold text-black hover:bg-gold-hover'
            }`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
